import { useEffect } from 'react';
import KBar from '@/components/kbar';
import AppSidebar from '@/components/layout/app-sidebar';
import Header from '@/components/layout/header';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { ThemeColorProvider } from '../theme-context';
// Auth removed - using custom auth
import { TourProvider } from '../tour-components';
import TourDefinition from '../tour-definition';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useGetWorkspaceQuery } from '@/services/workspaces/queries';
import { showToast } from '../ui/show-toast';
import { useGetOrganisation } from '@/services/organisations/queries';
import { SpotlightModal } from '../modal/spotlight-modal';

export default function DashboardLayout({
  defaultOpen
}: {
  defaultOpen: boolean;
}) {
  const activeWorkspace = useAuthStore(state => state.activeWorkspace)
  const user = useAuthStore(state => state.user)
  const context = useAuthStore(state => state.context)
  const showSpotlightModal = useAuthStore(state => state.showSpotlightModal)
  const setShowSpotlightModal = useAuthStore(state => state.setShowSpotlightModal)
  const navigate = useNavigate()
  
  useEffect(() => {
    if (!activeWorkspace || !context) {
      if (user) {
        // we will show this in the settings page
        // showToast('error', 'Something went wrong! You do not have any organisation or workspace');
        navigate('/settings', { replace: true });
      }
    }
  }, [activeWorkspace, context, user, navigate]); 

  const { data: workspace, isError: isErrorWorkspace } =  useGetWorkspaceQuery(activeWorkspace || '', {
    enabled: !!activeWorkspace,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  const { data: organisation, isError: isErrorOrganisation } = useGetOrganisation(user?.organisation?.id || '', {
    enabled: !!activeWorkspace && context === 'organisation',
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  const color = workspace?.color || '#1B3C8E';

  const userData = {
    context: context || '',
    email: user?.email || '',
    groupName: organisation?.name || workspace?.name,
    userRole: context === 'organisation' ? user?.organisation?.role : user?.workspace?.role,
    organisation: organisation,
    workspace: workspace,
  }

  useEffect(() => {
    if (isErrorWorkspace || isErrorOrganisation) {
      showToast('error', 'Something went wrong fetching your data');
      navigate('/settings', { replace: true });
    }
  }, [isErrorWorkspace, isErrorOrganisation, navigate]);

  return (
    <ThemeColorProvider color={color}>
      <KBar>
        <TourProvider>
          <TourDefinition />

          <SidebarProvider defaultOpen={defaultOpen}>
            <AppSidebar userData={userData} workspaceLogo={workspace?.logo || ''} />
            <SidebarInset>
              <Header />
              <Outlet />
            </SidebarInset>
          </SidebarProvider>
        </TourProvider>
      </KBar>
      <SpotlightModal
        open={showSpotlightModal && context === 'organisation'}
        onOpenChange={setShowSpotlightModal}
      />
    </ThemeColorProvider>
  );
}
